import type { PassportStation } from '../../api/passport';

// The backend LLM is *asked* to sort stations chronologically but does so
// unreliably. We enforce ordering on the client instead (most recent first,
// "present" on top) so the cover and the Stations page always agree.

// Textual open-ended markers (EN + DE). Backend normalizes most to "*-present";
// this catches leftovers and raw OCR-shaped strings.
const PRESENT_RE =
  /present|current|now|ongoing|heute|today|jetzt|aktuell|laufend|bis heute|seit/i;

const CURRENT_YEAR = new Date().getFullYear();

const years = (year: string | null): number[] => {
  if (!year) return [];
  const m = year.match(/\d{4}/g);
  return m ? m.map((x) => parseInt(x, 10)) : [];
};

/**
 * Ongoing if it literally says present/now/heute/... OR its end year is still in
 * the future (e.g. "2024-2027" while we're in 2026 -> the person is still there).
 * The Stations page keeps showing the literal range; only the "Now" treatment
 * (cover badge, emerald styling, top-of-timeline ordering) keys off this.
 */
export const isPresent = (year: string | null): boolean => {
  if (!year) return false;
  if (PRESENT_RE.test(year)) return true;
  const ys = years(year);
  return ys.length > 0 && ys[ys.length - 1] > CURRENT_YEAR;
};

/**
 * Display label for the Stations timeline. The literal word "present"/"heute"
 * etc. looks odd in the chip, so rewrite those to "<start> - Now". Numeric
 * ranges (incl. future ends like "2024-2027") are shown verbatim.
 */
export const formatStationYear = (year: string | null, nowLabel = 'Now'): string => {
  if (!year) return '';
  if (PRESENT_RE.test(year)) {
    const ys = years(year);
    return ys.length ? `${ys[0]} - ${nowLabel}` : nowLabel;
  }
  return year;
};

/** End year for recency ordering. Ongoing wins; else the last 4-digit year. */
export const recency = (year: string | null): number => {
  if (!year) return -1;
  if (isPresent(year)) return Number.MAX_SAFE_INTEGER;
  const ys = years(year);
  return ys.length ? ys[ys.length - 1] : -1;
};

/** Start year, used as a tie-breaker. */
const startYear = (year: string | null): number => {
  if (!year) return -1;
  const ys = years(year);
  return ys.length ? ys[0] : -1;
};

/** Newest first (present on top). Stable for equal keys. */
export const sortStationsByRecency = (stations: PassportStation[]): PassportStation[] =>
  [...stations].sort((a, b) => {
    const byEnd = recency(b.year) - recency(a.year);
    if (byEnd !== 0) return byEnd;
    return startYear(b.year) - startYear(a.year);
  });
